import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CourseGroupV2, CourseType } from '../../api/models/Course';
import { Student } from '../../api/models/Student';
import { Environment } from '../../Environment';
import { useCourseGroups } from '../../util/CourseGroupsHook';
import { useToken } from '../../util/GetTokenHook';
import { history } from '../../util/History';

export type StudentCourseDetailsProps = {
  // Outputs
  /** Course Group based on url parameter :course */
  courses: CourseGroupV2[];
  /** as the name implies */
  isGettingCourseGroups: boolean;
  /** current page */
  currentPage: number;
  /** getting student */
  isGettingStudent: boolean;
  /** the student object */
  student: Student | undefined;

  // Inputs
  backToOverview: () => void;
  userPressedCourseDetails: (courseType: CourseType) => void;
  userClickedPaginationDot: (currentPage: number) => void;
};

/**
 * Despite the name, this one is for the admin side
 */
export const studentCourseDetailsContainer = (Screen: React.ComponentType<StudentCourseDetailsProps>) => () => {
  const [courses, setCourses] = React.useState<CourseGroupV2[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isGettingStudent, setIsGettingStudent] = React.useState(false);
  const [student, setStudent] = React.useState<Student | undefined>(undefined);

  const { api } = Environment.current();
  const { course, id }: { course: CourseType; id: string } = useParams();

  const isMounted = useRef(true);
  const studentId = parseInt(id);

  // custom hooks
  const { token, isGettingToken } = useToken({ scope: 'admin:students' });
  const { courseGroups, isGettingCourseGroups } = useCourseGroups({ token, personId: studentId });

  // To prevent update on unmount
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // get courses from course groups
  React.useEffect(() => {
    if (course && courseGroups.length) {
      const courseType = course.toUpperCase();
      const filteredCourses = courseGroups.filter(group => group.courseGroupCode === courseType);
      if (isMounted.current) {
        setCourses(filteredCourses);
      }
    }
  }, [course, courseGroups.length]);

  // get student if it doesn't yet exist
  React.useEffect(() => {
    if (studentId && token && !student) {
      (async () => {
        try {
          setIsGettingStudent(true);
          const studentResult = await api.getStudentById(studentId, token);
          if (isMounted.current) {
            setStudent(studentResult);
            setIsGettingStudent(false);
          }
        } catch (e) {
          toast.error(e.message);
          setIsGettingStudent(false);
        }
      })();
    }
  }, [studentId, token, student]);

  return (
    <Screen
      student={student}
      isGettingStudent={isGettingStudent || isGettingToken}
      currentPage={currentPage}
      isGettingCourseGroups={isGettingCourseGroups || isGettingToken}
      courses={courses}
      backToOverview={() => {}}
      userClickedPaginationDot={setCurrentPage}
      userPressedCourseDetails={(courseType: CourseType) => {
        history.push(`/student-record/${studentId}/${courseType.toLowerCase()}`);
        setCurrentPage(0);
      }}
    />
  );
};
